import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
// import msbLogo from '../assets/images/msb_logo.svg';

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    users: 0,
    companies: 0,
    sessions: 0,
    storage: 0,
    lastActivity: 'Keine Aktivität',
  });

  const [recentActivity, setRecentActivity] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Lade Dashboard-Daten beim Start
  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);

      // Lade Server-Statistiken (Dummy-Daten für jetzt)
      setStats({
        users: 1,
        companies: 1,
        sessions: 1,
        storage: 0.1, // GB
        lastActivity: 'Heute',
      });

      // Lade letzte Aktivitäten (Dummy-Daten)
      const activities = [
        {
          id: 1,
          type: 'login',
          title: 'Benutzer angemeldet',
          description: 'Admin-Login erfolgreich',
          timestamp: new Date().toLocaleDateString('de-DE'),
          status: 'success',
        },
      ];
      setRecentActivity(activities);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Fallback zu Dummy-Daten bei Fehler
      setStats({
        users: 0,
        companies: 0,
        sessions: 0,
        storage: 0,
        lastActivity: 'Fehler beim Laden',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const quickActions = [
    {
      title: 'KI-Workspace',
      description: 'Künstliche Intelligenz für medizintechnische Anwendungen',
      icon: '🤖',
      href: '/ki-workspace',
      color: 'bg-purple-500 hover:bg-purple-600',
      stats: 'KI-Module',
    },
    {
      title: 'Benutzer & Firmen',
      description: 'Benutzer und Firmen verwalten',
      icon: '👥',
      href: '/users',
      color: 'bg-blue-500 hover:bg-blue-600',
      stats: 'Verwaltung',
    },
    {
      title: 'Einstellungen',
      description: 'System- und Server-Einstellungen verwalten',
      icon: '⚙️',
      href: '/settings',
      color: 'bg-gray-500 hover:bg-gray-600',
      stats: 'Konfigurieren',
    },
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return '✅';
      case 'warning':
        return '⚠️';
      case 'error':
        return '❌';
      case 'info':
        return 'ℹ️';
      default:
        return '📝';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-green-600 bg-green-100 dark:bg-green-900/20';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20';
      case 'error':
        return 'text-red-600 bg-red-100 dark:bg-red-900/20';
      case 'info':
        return 'text-blue-600 bg-blue-100 dark:bg-blue-900/20';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20';
    }
  };

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Willkommen zurück, {user?.firstName || user?.username || 'Benutzer'}! 👋
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Hier ist eine Übersicht über Ihr Rad.Link System
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500 dark:text-gray-400">Letzte Aktivität</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {stats.lastActivity}
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {quickActions.map((action, index) => (
          <Link
            key={index}
            to={action.href}
            className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 hover:shadow-lg transition-shadow duration-200 group"
          >
            <div className="flex items-center justify-between mb-4">
              <div
                className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl ${action.color} text-white`}
              >
                {action.icon}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                {action.stats}
              </div>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 group-hover:text-primary transition-colors">
              {action.title}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{action.description}</p>
          </Link>
        ))}
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                <span className="text-blue-600 text-lg">👥</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Benutzer</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.users}</p>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm">
              <span className="text-green-600">✓</span>
              <span className="text-gray-500 dark:text-gray-400 ml-1">Aktiv</span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                <span className="text-green-600 text-lg">🏢</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Firmen</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.companies}</p>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm">
              <span className="text-blue-600">📊</span>
              <span className="text-gray-500 dark:text-gray-400 ml-1">Registriert</span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                <span className="text-purple-600 text-lg">🔐</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Sessions</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.sessions}</p>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm">
              <span className="text-green-600">🟢</span>
              <span className="text-gray-500 dark:text-gray-400 ml-1">Online</span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center">
                <span className="text-orange-600 text-lg">💾</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Speicher</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.storage} GB</p>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm">
              <span className="text-blue-600">📈</span>
              <span className="text-gray-500 dark:text-gray-400 ml-1">Verwendet</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">Letzte Aktivitäten</h2>
        </div>
        <div className="p-6">
          {isLoading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
              <p className="text-gray-500 dark:text-gray-400 mt-2">Lade Aktivitäten...</p>
            </div>
          ) : recentActivity.length > 0 ? (
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${getStatusColor(activity.status)}`}
                  >
                    {getStatusIcon(activity.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {activity.title}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {activity.description}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      {activity.timestamp}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 text-4xl mb-4">📝</div>
              <p className="text-gray-500 dark:text-gray-400">Keine Aktivitäten vorhanden</p>
            </div>
          )}
        </div>
      </div>

      {/* System Status */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">System Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-green-500 text-3xl mb-2">🟢</div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">Server</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Online</p>
          </div>
          <div className="text-center">
            <div className="text-green-500 text-3xl mb-2">🟢</div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">Datenbank</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Verbunden</p>
          </div>
          <div className="text-center">
            <div className="text-blue-500 text-3xl mb-2">🔧</div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">Version</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">0.8.0</p>
          </div>
        </div>
      </div>
    </div>
  );
}
